/**
 * Predicats partages decrivant la filtrabilite d'une colonne de tableau
 * dans la section Filter ( cf. `FiltersDataCollapse.vue` ) .
 *
 * Single source of truth pour :
 *  - quel composant rendre par colonne ( ReferenceFilter , TextFilter ,
 *    ListFilter , FilterNumberOrDate ) ;
 *  - si la section Filter doit afficher le placeholder "Aucun filtre" ou
 *    les widgets + boutons reset / afficher resultats .
 *
 * Extrait de FiltersDataCollapse.vue pour :
 *  - SRP : 1 module = 1 responsabilite ( predicats purs ) ;
 *  - DRY : eviter la divergence entre la logique du template ( v-if ) et
 *    celle de `showSectionFilter` ( bug #519 : la branche FILTER_TEXT /
 *    FILTER_LIST rendait les widgets sans pour autant compter les
 *    colonnes dans le count -> placeholder + boutons absents ) ;
 *  - Testabilite : fonctions pures , independantes de Vue / Buefy /
 *    backend , trivialement unit-testables .
 *
 * @see ticket #519 - filtres autogeneres FILTER_TEXT / FILTER_LIST sur
 *      des valeurs non requises ( colonne sans checker dans la conf
 *      YAML ) etaient ignores par showSectionFilter , masquant les
 *      boutons reset + afficher resultats .
 */

/**
 * Tag definition exposees par le backend dans `column.tags[].tagDefinition`
 * et controlant l'activation opt-in du filtre par colonne ( YAML : OA_tags ) .
 *
 * Extrait en constantes pour eviter les magic strings dispersees dans le
 * code et faciliter l'ajout d'un nouveau type de tag dans le futur .
 */
export const FILTER_TEXT_TAG = "FILTER_TEXT_TAG" as const;
export const FILTER_LIST_TAG = "FILTER_LIST_TAG" as const;

/**
 * Types de checker historiquement consideres "non filtrables par defaut" .
 * Une colonne avec un de ces checkers ne devient filtrable que si elle
 * porte un tag FILTER_TEXT / FILTER_LIST opt-in .
 */
const NON_AUTO_FILTERABLE_CHECKER_TYPES = new Set<string>([
  "StringChecker",
  "BooleanChecker",
]);

/**
 * Forme minimale d'une colonne consommee par les predicats ci-dessous .
 * Volontairement laxiste ( index signature ) pour s'aligner sur la
 * structure dynamique des colonnes provenant du backend , tout en
 * documentant les champs reellement utilises .
 */
export interface FilterableColumn {
  id?: string;
  componentKey?: string;
  filterable?: boolean;
  filterableAsText?: boolean;
  checker?: { type?: string } | null;
  tags?: Array<{ tagDefinition?: string }>;
  [key: string]: unknown;
}

/**
 * Vrai si la colonne porte un tag de filtre opt-in ( FILTER_TEXT ou
 * FILTER_LIST ) , avec fallback sur l'inspection des tags si le backend
 * n'expose pas la propriete `filterable` derivee .
 *
 * Privilegier la propriete sérialisée garantit l'alignement avec la
 * logique d'exclusion calculee cote backend ( ex : checker reference
 * desactive le filtre opt-in dans certains cas ) .
 */
export function hasFilterTag(column: FilterableColumn | null | undefined): boolean {
  if (!column) return false;
  if (typeof column.filterable === "boolean") return column.filterable;
  return Array.isArray(column.tags)
    && column.tags.some((t) =>
      t?.tagDefinition === FILTER_TEXT_TAG
      || t?.tagDefinition === FILTER_LIST_TAG);
}

/**
 * Vrai si la colonne doit etre rendue avec un TextFilter ( recherche
 * "contient" - tag __FILTER_TEXT__ ) .
 */
export function hasFilterTextTag(column: FilterableColumn | null | undefined): boolean {
  if (!column) return false;
  if (typeof column.filterableAsText === "boolean") return column.filterableAsText;
  return Array.isArray(column.tags)
    && column.tags.some((t) => t?.tagDefinition === FILTER_TEXT_TAG);
}

/**
 * Vrai si la colonne doit etre rendue avec un ListFilter ( dropdown
 * searchable - tag __FILTER_LIST__ ) . Derive de la semantique ternaire
 * "pas de filtre / text / list" : filterable mais pas as-text => list .
 */
export function hasFilterListTag(column: FilterableColumn | null | undefined): boolean {
  return hasFilterTag(column) && !hasFilterTextTag(column);
}

/**
 * Vrai si la colonne va produire un widget de filtre dans la section
 * Filter . Mirror exact des branches `v-if / v-else-if` de
 * `FiltersDataCollapse.vue` :
 *
 *  1. tag opt-in FILTER_TEXT / FILTER_LIST -> filtrable independamment
 *     du checker ( ticket #519 : colonnes non requises sans checker ) ;
 *  2. checker present et non String / Boolean ( Reference , Float ,
 *     Integer , Date , ... ) -> filtrable ;
 *  3. sinon -> non filtrable .
 *
 * La colonne virtuelle `id === '#'` ne porte pas de widget de filtre
 * via la branche tag opt-in ( cf template ligne 216 ) .
 */
export function isColumnFilterable(column: FilterableColumn | null | undefined): boolean {
  if (!column) return false;
  if (hasFilterTag(column)) {
    return column.id !== "#";
  }
  const type = column.checker?.type;
  if (!type) return false;
  return !NON_AUTO_FILTERABLE_CHECKER_TYPES.has(type);
}

/**
 * Vrai si au moins une colonne du dataset porte un widget de filtre .
 * Pilote :
 *  - le placeholder "Aucun filtre n'est disponible" ( affiche si false ) ;
 *  - la visibilite des boutons "Reinitialiser les filtres" et "Afficher
 *    les resultats" du card-footer .
 */
export function columnsHaveFilter(
  columns: ReadonlyArray<FilterableColumn> | null | undefined,
): boolean {
  return Array.isArray(columns) && columns.some(isColumnFilterable);
}
